import { useMemo, useState } from 'react'
import useDealStore from '../store/dealStore'
import '../styles/module16.css'

const money = (v) => `$${Math.round(Number(v) || 0).toLocaleString()}`
const pct = (v, digits = 1) => `${(Number(v) || 0).toFixed(digits)}%`
const multiple = (v) => `${(Number(v) || 0).toFixed(2)}x`
const today = () => new Date().toISOString().split('T')[0]
const fmtDate = (d) => d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'
const safeDiv = (a, b) => Number(b) ? Number(a) / Number(b) : 0

const TAX = {
  capitalGains: 0.2,
  depreciationRecapture: 0.25,
  state: 0.05,
}

const scenarioPresets = {
  conservative: { label: 'Conservative', noiGrowth: 2, exitCapSpread: 0.6, sellingCost: 4.5 },
  base: { label: 'Base Case', noiGrowth: 3, exitCapSpread: 0.15, sellingCost: 4 },
  optimistic: { label: 'Optimistic', noiGrowth: 4.25, exitCapSpread: -0.25, sellingCost: 3.5 },
}

const completionSteps = [
  { id: 'performance_packet', label: 'Performance history from Module 15 reviewed' },
  { id: 'sale_refi_compare', label: 'Sale, refinance, hold, and 1031 paths compared' },
  { id: 'tax_review', label: 'Tax basis and depreciation recapture reviewed with advisor' },
  { id: 'investor_report', label: 'Investor exit report prepared' },
  { id: 'execution_timeline', label: 'Exit timeline and next actions assigned' },
]

export default function Module16Page() {
  const { dealState, updateDealState, updateAvailableModules } = useDealStore()

  const raw = dealState.rawDealData || {}
  const fin = dealState.extractedFinancials || {}
  const uw = dealState.underwritingMetrics || {}
  const closing = dealState.closingDetails || {}
  const asset = dealState.assetManagement || {}
  const saved = dealState.exitStrategy || {}
  const ownershipCosts = dealState.ownershipCosts || {}

  const propName = raw.location?.address || dealState.dealName || 'Subject Property'
  const location = raw.location?.city && raw.location?.state ? `${raw.location.city}, ${raw.location.state}` : ''
  const units = Number(raw.property?.units) || Number(fin.revenue?.rentRoll?.totalUnits) || 0
  const purchasePrice = Number(raw.price) || 0
  const loanAmount = Number(uw.debtService?.loanAmount) || Math.round(purchasePrice * 0.7)
  const equityInvested = Math.max(purchasePrice - loanAmount, 1)
  const annualDebtService = Number(uw.debtService?.annual) || Number(uw.debtService?.monthly) * 12 || loanAmount * 0.075
  const entryNOI = Number(asset.kpiDashboard?.noi) || Number(fin.noi) || 0
  const entryCapRate = Number(uw.capRate?.entry) || safeDiv(entryNOI, purchasePrice) * 100
  const closingDate = closing.closingDate || today()
  const hold = asset.holdStrategy || {}

  const [activeTab, setActiveTab] = useState('overview')
  const [selectedScenario, setSelectedScenario] = useState(saved.scenario || 'base')
  const initialStrategy = saved.recommendation || (hold.strategy === 'hold_long' ? 'hold' : hold.strategy) || 'sell'
  const [strategy, setStrategy] = useState(initialStrategy)
  const [holdYears, setHoldYears] = useState(saved.exitYear || hold.years || 5)
  const [exitCapRate, setExitCapRate] = useState(saved.exitCapRate || hold.exitCapRate || Math.max(entryCapRate + 0.15, 4.5).toFixed(2))
  const [noiGrowth, setNoiGrowth] = useState(saved.noiGrowth || scenarioPresets.base.noiGrowth)
  const [sellingCostPct, setSellingCostPct] = useState(saved.sellingCostPct || scenarioPresets.base.sellingCost)
  const [refiLtv, setRefiLtv] = useState(saved.refiLtv || 70)
  const [refiRate, setRefiRate] = useState(saved.refiRate || Number(uw.debtService?.interestRate) || 6.75)
  const [qiName, setQiName] = useState(saved.qiName || '')
  const [exchangeTarget, setExchangeTarget] = useState(saved.exchangeTarget || '')
  const [notes, setNotes] = useState(saved.notes || '')
  const [checkedSteps, setCheckedSteps] = useState(saved.checkedSteps || [])
  const [saving, setSaving] = useState(false)
  const [completed, setCompleted] = useState(Boolean(saved.completedAt))
  const [landPct, setLandPct] = useState(saved.landPct || 20)

  const model = useMemo(() => {
    const years = Math.max(Number(holdYears) || 1, 1)
    const growth = Number(noiGrowth) || 0
    const cap = Math.max(Number(exitCapRate) || entryCapRate || 6, 0.1)
    const exitNOI = entryNOI * Math.pow(1 + growth / 100, years)
    const exitValue = exitNOI / (cap / 100)
    const remainingLoan = loanAmount * Math.pow(0.97, years)
    const sellingCosts = exitValue * (Number(sellingCostPct) / 100)
    const grossSaleProceeds = exitValue - sellingCosts - remainingLoan
    const depreciation = purchasePrice ? (purchasePrice / 27.5) * years : 0
    const taxableGain = Math.max(exitValue - purchasePrice, 0)
    const taxLiability = taxableGain * (TAX.capitalGains + TAX.state) + depreciation * TAX.depreciationRecapture
    const saleNet = grossSaleProceeds - taxLiability
    const equityMultiple = saleNet > 0 ? (saleNet + annualDebtService * 0.12 * years) / equityInvested : 0
    const saleIRR = saleNet > 0 ? (Math.pow((saleNet + equityInvested) / equityInvested, 1 / years) - 1) * 100 : 0

    const newLoan = exitValue * (Number(refiLtv) / 100)
    const cashOut = Math.max(newLoan - remainingLoan, 0)
    const monthlyRate = Number(refiRate) / 100 / 12
    const refiPayment = monthlyRate
      ? (newLoan * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -360))
      : newLoan / 360
    const refiDscr = safeDiv(exitNOI / 12, refiPayment)
    const holdCashFlow = Math.max(exitNOI - annualDebtService, 0)
    const exchangeEquity = grossSaleProceeds

    return {
      years,
      exitNOI,
      exitValue,
      remainingLoan,
      sellingCosts,
      grossSaleProceeds,
      taxLiability,
      saleNet,
      saleIRR,
      equityMultiple,
      newLoan,
      cashOut,
      refiPayment,
      refiDscr,
      holdCashFlow,
      exchangeEquity,
      deferredTax: taxLiability,
    }
  }, [annualDebtService, entryCapRate, entryNOI, equityInvested, exitCapRate, holdYears, loanAmount, noiGrowth, purchasePrice, refiLtv, refiRate, sellingCostPct])

  const scenarios = useMemo(() => {
    return Object.entries(scenarioPresets).map(([id, preset]) => {
      const cap = Math.max(entryCapRate + preset.exitCapSpread, 4)
      const years = Number(holdYears) || 5
      const exitNOI = entryNOI * Math.pow(1 + preset.noiGrowth / 100, years)
      const exitValue = exitNOI / (cap / 100)
      const remainingLoan = loanAmount * Math.pow(0.97, years)
      const costs = exitValue * (preset.sellingCost / 100)
      const tax = Math.max(exitValue - purchasePrice, 0) * (TAX.capitalGains + TAX.state)
      const net = exitValue - remainingLoan - costs - tax
      const irr = net > 0 ? (Math.pow((net + equityInvested) / equityInvested, 1 / years) - 1) * 100 : 0
      return { id, ...preset, exitCapRate: cap, exitNOI, exitValue, net, irr }
    })
  }, [entryCapRate, entryNOI, equityInvested, holdYears, loanAmount, purchasePrice])

  const recommendation = useMemo(() => {
    if (model.refiDscr < 1.25 && strategy === 'refinance') return 'Sale or 1031 exchange is stronger because refinance DSCR is below lender comfort.'
    if (strategy === 'hold') return 'Holding longer is defensible if cash flow remains strong and the market has not reached the target exit window.'
    if (model.deferredTax > model.cashOut && model.saleNet > 0) return 'A 1031 exchange is attractive if the investor wants to keep compounding and defer taxes.'
    if (model.cashOut > model.saleNet * 0.55 && model.refiDscr >= 1.25) return 'Cash-out refinance preserves ownership while returning meaningful equity.'
    return 'A market sale is the cleanest exit if liquidity and investor distribution are the priority.'
  }, [model.cashOut, model.deferredTax, model.refiDscr, model.saleNet, strategy])

  function applyScenario(id) {
    const preset = scenarioPresets[id]
    setSelectedScenario(id)
    setNoiGrowth(preset.noiGrowth)
    setExitCapRate(Math.max(entryCapRate + preset.exitCapSpread, 4).toFixed(2))
    setSellingCostPct(preset.sellingCost)
  }

  function toggleStep(id) {
    setCheckedSteps(prev => prev.includes(id) ? prev.filter(step => step !== id) : [...prev, id])
  }

  function buildPayload(completing = false) {
    return {
      analysis: {
        sell: {
          roi: safeDiv(model.saleNet, purchasePrice) * 100,
          irr: model.saleIRR,
          equityMultiple: model.equityMultiple,
          timing: String(holdYears),
          projectedValue: model.exitValue,
          netProceeds: model.saleNet,
          taxLiability: model.taxLiability,
        },
        refinance: {
          roi: safeDiv(model.cashOut, equityInvested) * 100,
          timing: String(holdYears),
          cashOut: model.cashOut,
          newLoan: model.newLoan,
          dscr: model.refiDscr,
        },
        hold: {
          annualCashFlow: model.holdCashFlow,
          projectedNOI: model.exitNOI,
          projectedValue: model.exitValue,
        },
        exchange1031: {
          deferredTax: model.deferredTax,
          reinvestableEquity: model.exchangeEquity,
          qiName,
          exchangeTarget,
        },
      },
      recommendation: strategy,
      aiRecommendation: recommendation,
      scenario: selectedScenario,
      exitTimeline: String(holdYears),
      exitYear: String(holdYears),
      exitCapRate: String(exitCapRate),
      noiGrowth: String(noiGrowth),
      sellingCostPct: String(sellingCostPct),
      refiLtv: String(refiLtv),
      refiRate: String(refiRate),
      qiName,
      exchangeTarget,
      notes,
      checkedSteps,
      completedAt: completing ? new Date().toISOString() : saved.completedAt || null,
    }
  }

  function handleSave() {
    updateDealState({ exitStrategy: buildPayload(false) })
  }

  function handleComplete() {
    setSaving(true)
    updateDealState({
      exitStrategy: buildPayload(true),
      pipelineStage: 17,
      pipelineStatus: 'completed',
    })
    updateAvailableModules()
    setTimeout(() => {
      setSaving(false)
      setCompleted(true)
    }, 600)
  }

  const strategyOptions = [
    {
      id: 'sell',
      label: 'Sell',
      headline: money(model.saleNet),
      note: 'Net proceeds after selling costs, loan payoff, and estimated taxes',
      metrics: [
        ['Exit Value', money(model.exitValue)],
        ['Net Proceeds', money(model.saleNet)],
        ['Projected IRR', pct(model.saleIRR)],
        ['Equity Multiple', multiple(model.equityMultiple)],
      ],
    },
    {
      id: 'refinance',
      label: 'Cash-Out Refi',
      headline: money(model.cashOut),
      note: 'Estimated tax-free refinance proceeds while retaining ownership',
      metrics: [
        ['New Loan', money(model.newLoan)],
        ['Cash Out', money(model.cashOut)],
        ['New DSCR', multiple(model.refiDscr)],
        ['Monthly Debt', money(model.refiPayment)],
      ],
    },
    {
      id: 'exchange_1031',
      label: '1031 Exchange',
      headline: money(model.deferredTax),
      note: 'Estimated tax deferred into a replacement property',
      metrics: [
        ['Deferred Tax', money(model.deferredTax)],
        ['Reinvestable Equity', money(model.exchangeEquity)],
        ['ID Deadline', '45 days'],
        ['Close Deadline', '180 days'],
      ],
    },
    {
      id: 'hold',
      label: 'Hold Longer',
      headline: money(model.holdCashFlow),
      note: 'Projected annual cash flow if ownership continues beyond the target exit window',
      metrics: [
        ['NOI at Exit', money(model.exitNOI)],
        ['Annual Cash Flow', money(model.holdCashFlow)],
        ['Remaining Debt', money(model.remainingLoan)],
        ['Projected Value', money(model.exitValue)],
        ...(ownershipCosts.totalMonthlyOwnershipCost > 0 ? [['Total Monthly Cost', money(ownershipCosts.totalMonthlyOwnershipCost)]] : []),
        ...(ownershipCosts.annualInsurance > 0 ? [[`Insurance${ownershipCosts.insuranceIsEstimated ? ' (est.)' : ''}/yr`, money(ownershipCosts.annualInsurance)]] : []),
      ],
    },
  ]

  if (completed) {
    return (
      <div className="m16-celebration">
        <div className="m16-celebration-card">
          <div className="m16-celebration-trophy">
            <svg width="96" height="96" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="48" cy="48" r="48" fill="url(#grad1)"/>
              <circle cx="48" cy="48" r="38" fill="rgba(255,255,255,0.12)"/>
              <polyline points="28,50 42,64 68,34" stroke="white" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              <defs>
                <linearGradient id="grad1" x1="0" y1="0" x2="96" y2="96" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#1d4ed8"/>
                  <stop offset="100%" stopColor="#3b82f6"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
          <h1>Investment Lifecycle Complete</h1>
          <p className="m16-celebration-prop">{propName}{location && `, ${location}`}</p>
          <div className="m16-celebration-stats">
            <div className="m16-cel-stat"><div className="m16-cel-val">{money(purchasePrice)}</div><div className="m16-cel-label">Acquisition</div></div>
            <div className="m16-cel-stat"><div className="m16-cel-val">{money(model.exitValue)}</div><div className="m16-cel-label">Exit Value</div></div>
            <div className="m16-cel-stat"><div className="m16-cel-val">{pct(model.saleIRR)}</div><div className="m16-cel-label">Projected IRR</div></div>
            <div className="m16-cel-stat"><div className="m16-cel-val">{model.years} yrs</div><div className="m16-cel-label">Hold</div></div>
          </div>
          <div className="m16-celebration-strategy">
            Final strategy: <strong>{strategyOptions.find(s => s.id === strategy)?.label || strategy}</strong>
          </div>
          <div className="m16-celebration-badge">All AcquireOS AI modules complete</div>
          <div className="m16-optional-note">
            💡 <strong>Note:</strong> Module 6 (Deal Syndication Engine) is optional — use it only if you choose to syndicate the deal with other investors.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="m16-page">
      <div className="m16-header">
        <div className="m16-header-left">
          <div className="m16-module-badge">Module 16</div>
          <div>
            <h1>Exit Strategy</h1>
            <p className="m16-sub">{propName}{location && ` - ${location}`} - {units || '-'} units - final module</p>
          </div>
        </div>
        <div className="m16-header-right">
          <button className="m16-save-btn" onClick={handleSave}>Save Progress</button>
        </div>
      </div>

      <div className="m16-strip">
        {[
          ['Purchase Price', money(purchasePrice)],
          ['Current NOI', money(entryNOI)],
          ['Exit Value', money(model.exitValue)],
          ['Net Sale Proceeds', money(model.saleNet)],
          ['Cash-Out Refi', money(model.cashOut)],
          ['Exit Cap Rate', pct(exitCapRate, 2)],
          ...(ownershipCosts.totalMonthlyOwnershipCost > 0 ? [['Total Monthly Cost', money(ownershipCosts.totalMonthlyOwnershipCost)]] : []),
          ...(ownershipCosts.annualInsurance > 0 ? [[`Insurance${ownershipCosts.insuranceIsEstimated ? ' (est.)' : ''}/yr`, money(ownershipCosts.annualInsurance)]] : []),
        ].map(([label, val]) => (
          <div key={label} className="m16-strip-item">
            <div className="m16-strip-val">{val}</div>
            <div className="m16-strip-label">{label}</div>
          </div>
        ))}
      </div>

      <div className="m16-tabs">
        {[
          ['overview',     'Strategy Comparison'],
          ['scenarios',    'Scenario Modeling'],
          ['refinance',    'Refinance Analysis'],
          ['exchange',     '1031 Exchange'],
          ['depreciation', '📉 Depreciation'],
          ['irrmap',       '🗺️ IRR Heat Map'],
          ['report',       'Investor Report'],
        ].map(([id, label]) => (
          <button key={id} className={`m16-tab ${activeTab === id ? 'active' : ''}`} onClick={() => setActiveTab(id)}>
            {label}
          </button>
        ))}
      </div>

      <div className="m16-content">
        {activeTab === 'overview' && (
          <div className="m16-overview-panel">
            <div className="m16-section-header">
              <h3>Grand Finale Exit Planner</h3>
              <p>Compare sale, cash-out refinance, and 1031 exchange options using Module 15 performance and hold assumptions.</p>
            </div>

            <div className="m16-form-grid">
              <div className="m16-form-group">
                <label>Target Hold Period (Years)</label>
                <input className="m16-input" type="number" min="1" max="30" value={holdYears} onChange={e => setHoldYears(e.target.value)} />
              </div>
              <div className="m16-form-group">
                <label>Exit Cap Rate (%)</label>
                <input className="m16-input" type="number" step="0.05" value={exitCapRate} onChange={e => setExitCapRate(e.target.value)} />
              </div>
              <div className="m16-form-group">
                <label>Annual NOI Growth (%)</label>
                <input className="m16-input" type="number" step="0.25" value={noiGrowth} onChange={e => setNoiGrowth(e.target.value)} />
              </div>
              <div className="m16-form-group">
                <label>Selling Costs (%)</label>
                <input className="m16-input" type="number" step="0.25" value={sellingCostPct} onChange={e => setSellingCostPct(e.target.value)} />
              </div>
            </div>

            <div className="m16-strategy-cards">
              {strategyOptions.map(option => (
                <button key={option.id} className={`m16-strategy-card ${strategy === option.id ? 'selected' : ''}`} onClick={() => setStrategy(option.id)}>
                  <div className="m16-sc-top">
                    <span className="m16-sc-label">{option.label}</span>
                    {strategy === option.id && <span className="m16-sc-chosen">Selected</span>}
                  </div>
                  <div className="m16-sc-headline">{option.headline}</div>
                  <div className="m16-sc-headnote">{option.note}</div>
                  <div className="m16-sc-metrics">
                    {option.metrics.map(([label, val]) => (
                      <div key={label} className="m16-sc-metric">
                        <span className="m16-sc-metric-label">{label}</span>
                        <span className="m16-sc-metric-val">{val}</span>
                      </div>
                    ))}
                  </div>
                </button>
              ))}
            </div>

            <div className="m16-selection-confirm">
              <span className="m16-sel-icon">AI</span>
              <span><strong>Risk-adjusted advice:</strong> {recommendation}</span>
            </div>
          </div>
        )}

        {activeTab === 'scenarios' && (
          <div className="m16-overview-panel">
            <div className="m16-section-header">
              <h3>Scenario Modeling Engine</h3>
              <p>Run conservative, base, and optimistic assumptions to see return sensitivity before choosing the exit window.</p>
            </div>

            <div className="m16-strategy-cards">
              {scenarios.map(s => (
                <button key={s.id} className={`m16-strategy-card ${selectedScenario === s.id ? 'selected' : ''}`} onClick={() => applyScenario(s.id)}>
                  <div className="m16-sc-top">
                    <span className="m16-sc-label">{s.label}</span>
                    {selectedScenario === s.id && <span className="m16-sc-chosen">Applied</span>}
                  </div>
                  <div className="m16-sc-headline">{money(s.net)}</div>
                  <div className="m16-sc-headnote">Estimated net sale proceeds</div>
                  <div className="m16-sc-metrics">
                    <div className="m16-sc-metric"><span>NOI Growth</span><strong>{pct(s.noiGrowth)}</strong></div>
                    <div className="m16-sc-metric"><span>Exit Cap</span><strong>{pct(s.exitCapRate, 2)}</strong></div>
                    <div className="m16-sc-metric"><span>Exit Value</span><strong>{money(s.exitValue)}</strong></div>
                    <div className="m16-sc-metric"><span>IRR</span><strong>{pct(s.irr)}</strong></div>
                  </div>
                </button>
              ))}
            </div>

            <div className="m16-compare-table-wrap">
              <h4>Return Projection Comparison</h4>
              <table className="m16-compare-table">
                <thead>
                  <tr><th>Scenario</th><th>NOI at Exit</th><th>Sale Value</th><th>Net Proceeds</th><th>Projected IRR</th></tr>
                </thead>
                <tbody>
                  {scenarios.map(s => (
                    <tr key={s.id}>
                      <td className="m16-compare-label">{s.label}</td>
                      <td>{money(s.exitNOI)}</td>
                      <td>{money(s.exitValue)}</td>
                      <td>{money(s.net)}</td>
                      <td>{pct(s.irr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'refinance' && (
          <div className="m16-refi-panel">
            <div className="m16-section-header">
              <h3>Cash-Out Refinance Analysis</h3>
              <p>Evaluate how much equity can be pulled out while maintaining healthy debt service coverage.</p>
            </div>
            <div className="m16-form-grid">
              <div className="m16-form-group">
                <label>Refi LTV (%)</label>
                <input className="m16-input" type="number" min="50" max="80" value={refiLtv} onChange={e => setRefiLtv(e.target.value)} />
              </div>
              <div className="m16-form-group">
                <label>Refi Interest Rate (%)</label>
                <input className="m16-input" type="number" step="0.125" value={refiRate} onChange={e => setRefiRate(e.target.value)} />
              </div>
            </div>
            <div className="m16-refi-cards">
              {[
                ['Tax-Free Cash Out', money(model.cashOut), 'Loan proceeds are generally not taxable income'],
                ['New Loan Amount', money(model.newLoan), `${refiLtv}% LTV against projected value`],
                ['New DSCR', multiple(model.refiDscr), model.refiDscr >= 1.25 ? 'Above common lender minimum' : 'Below common lender comfort level'],
                ['Monthly Debt Service', money(model.refiPayment), `At ${pct(refiRate, 2)} over 30 years`],
                ['Annual Cash Flow', money(model.exitNOI - model.refiPayment * 12), 'After refinance debt service'],
                ['Equity Retained', money(model.exitValue - model.newLoan), 'Remaining ownership equity after refinance'],
              ].map(([label, val, note]) => (
                <div key={label} className="m16-refi-card">
                  <div className="m16-refi-val">{val}</div>
                  <div className="m16-refi-label">{label}</div>
                  <div className="m16-refi-note">{note}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'exchange' && (
          <div className="m16-exchange-panel">
            <div className="m16-section-header">
              <h3>1031 Exchange Readiness</h3>
              <p>Prepare the tax-deferred path into a replacement property with strict deadline tracking.</p>
            </div>
            <div className="m16-exchange-cards">
              {[
                ['Deferred Tax', money(model.deferredTax), 'Taxes deferred, not eliminated'],
                ['Reinvestable Equity', money(model.exchangeEquity), 'Estimated equity available for upleg'],
                ['Identification Deadline', '45 Days', 'Identify replacement property after downleg close'],
                ['Closing Deadline', '180 Days', 'Close replacement property after downleg close'],
              ].map(([label, val, note]) => (
                <div key={label} className="m16-exchange-card">
                  <div className="m16-exc-val">{val}</div>
                  <div className="m16-exc-label">{label}</div>
                  <div className="m16-exc-note">{note}</div>
                </div>
              ))}
            </div>
            <div className="m16-form-grid">
              <div className="m16-form-group">
                <label>Qualified Intermediary</label>
                <input className="m16-input" value={qiName} onChange={e => setQiName(e.target.value)} placeholder="QI company or contact" />
              </div>
              <div className="m16-form-group">
                <label>Target Upleg / Market</label>
                <input className="m16-input" value={exchangeTarget} onChange={e => setExchangeTarget(e.target.value)} placeholder="Replacement property criteria" />
              </div>
            </div>
            <div className="m16-exchange-rules">
              <h4>Readiness Checklist</h4>
              {[
                'Engage a Qualified Intermediary before sale proceeds are received.',
                'Confirm replacement property is like-kind real property.',
                'Identify upleg options within 45 days of the downleg closing.',
                'Close on the replacement asset within 180 days.',
                'Avoid taxable boot by reinvesting equal or greater value and equity.',
                'Coordinate with CPA and exchange counsel before execution.',
              ].map((rule, index) => (
                <div key={rule} className="m16-rule-item">
                  <span className="m16-rule-num">{index + 1}</span>
                  <span>{rule}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── DEPRECIATION SCHEDULE ─────────────────────────── */}
        {activeTab === 'depreciation' && (() => {
          const buildingValue = purchasePrice * (1 - landPct / 100)
          const annualDepr    = buildingValue / 27.5
          const yearsShown    = Math.min(Number(holdYears) + 3, 30)
          const exitYr        = Number(holdYears)
          const schedule      = Array.from({ length: yearsShown }, (_, i) => {
            const yr     = i + 1
            const cumDep = annualDepr * yr
            const recapture = cumDep * TAX.depreciationRecapture
            return { yr, annualDepr, cumDep, recapture }
          })
          return (
            <div className="m16-depr-panel">
              <div className="m16-section-header">
                <h3>Depreciation Schedule</h3>
                <p>27.5-year straight-line residential depreciation. Adjust land value percentage to match your cost segregation or appraisal.</p>
              </div>
              <div className="m16-depr-controls">
                <div className="m16-form-group">
                  <label>Land Value % of Purchase Price</label>
                  <input className="m16-input" type="number" min="0" max="60" step="1"
                    value={landPct} onChange={(e) => setLandPct(Number(e.target.value))} />
                </div>
                <div className="m16-depr-sum-cards">
                  {[
                    { label: 'Building Value',   val: money(buildingValue) },
                    { label: 'Annual Depr.',     val: money(annualDepr) },
                    { label: `Cum. Depr. Yr ${exitYr}`, val: money(annualDepr * exitYr) },
                    { label: 'Recapture Tax Est.',val: money(annualDepr * exitYr * TAX.depreciationRecapture) },
                  ].map((c) => (
                    <div key={c.label} className="m16-depr-sum-card">
                      <div className="m16-depr-sum-val">{c.val}</div>
                      <div className="m16-depr-sum-label">{c.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <table className="m16-depr-table">
                <thead>
                  <tr><th>Year</th><th>Annual Depr.</th><th>Cumulative Depr.</th><th>Recapture @ 25%</th></tr>
                </thead>
                <tbody>
                  {schedule.map((row) => (
                    <tr key={row.yr} className={row.yr === exitYr ? 'm16-depr-exit' : ''}>
                      <td>{row.yr === exitYr ? `★ Yr ${row.yr} (Exit)` : `Year ${row.yr}`}</td>
                      <td>{money(row.annualDepr)}</td>
                      <td>{money(row.cumDep)}</td>
                      <td>{money(row.recapture)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        })()}

        {/* ── IRR HEAT MAP ───────────────────────────────────── */}
        {activeTab === 'irrmap' && (() => {
          const HOLD_YEARS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
          const CAP_RATES  = [4.0, 4.5, 5.0, 5.5, 6.0, 6.5, 7.0, 7.5, 8.0]
          const calcIRR = (yr, cap) => {
            const exitNOI2  = entryNOI * Math.pow(1 + Number(noiGrowth) / 100, yr)
            const exitVal2  = cap > 0 ? exitNOI2 / (cap / 100) : 0
            const remLoan   = loanAmount * Math.pow(0.97, yr)
            const costs     = exitVal2 * (Number(sellingCostPct) / 100)
            const net       = exitVal2 - remLoan - costs - Math.max(exitVal2 - purchasePrice, 0) * (TAX.capitalGains + TAX.state)
            return net > 0 ? (Math.pow((net + equityInvested) / equityInvested, 1 / yr) - 1) * 100 : 0
          }
          const cellStyle = (irr) => {
            if (irr >= 18) return { bg: '#1e3a8a', text: '#fff' }
            if (irr >= 14) return { bg: '#1d4ed8', text: '#fff' }
            if (irr >= 10) return { bg: '#2563eb', text: '#fff' }
            if (irr >=  6) return { bg: '#3b82f6', text: '#fff' }
            if (irr >=  4) return { bg: '#60a5fa', text: '#fff' }
            return { bg: '#93c5fd', text: '#fff' }
          }
          return (
            <div className="m16-irrmap-panel">
              <div className="m16-section-header">
                <h3>IRR Heat Map</h3>
                <p>Projected IRR across hold periods and exit cap rates using current NOI growth assumption. Black outline = your current selection.</p>
              </div>
              <div className="m16-irrmap-wrap">
                <table className="m16-irrmap-table">
                  <thead>
                    <tr>
                      <th>Hold ↓ / Exit Cap →</th>
                      {CAP_RATES.map((c) => <th key={c}>{c.toFixed(1)}%</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {HOLD_YEARS.map((yr) => (
                      <tr key={yr}>
                        <td className="m16-irrmap-yr">{yr}yr</td>
                        {CAP_RATES.map((cap) => {
                          const irr      = calcIRR(yr, cap)
                          const style    = cellStyle(irr)
                          const isCurrent = yr === Number(holdYears) && Math.abs(cap - Number(exitCapRate)) < 0.3
                          return (
                            <td key={cap}
                              className="m16-irrmap-cell"
                              style={{
                                background: style.bg,
                                boxShadow: isCurrent ? 'inset 0 0 0 3px #0f172a' : undefined,
                              }}
                            >
                              {irr.toFixed(1)}%
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="m16-irrmap-legend">
                {[
                  { bg: '#93c5fd', text: '#fff', label: '< 4%' },
                  { bg: '#60a5fa', text: '#fff', label: '4–6%' },
                  { bg: '#3b82f6', text: '#fff', label: '6–10%' },
                  { bg: '#2563eb', text: '#fff', label: '10–14%' },
                  { bg: '#1d4ed8', text: '#fff', label: '14–18%' },
                  { bg: '#1e3a8a', text: '#fff', label: '≥ 18%' },
                ].map((l) => (
                  <span key={l.label} className="m16-irrmap-leg-item" style={{ background: l.bg, color: l.text }}>{l.label}</span>
                ))}
              </div>
              <p className="m16-irrmap-insight">
                At your current {holdYears}-year hold and {exitCapRate}% exit cap, the modeled IRR is{' '}
                <strong>{calcIRR(Number(holdYears), Number(exitCapRate)).toFixed(1)}%</strong>.
                {calcIRR(Number(holdYears), Number(exitCapRate)) < 10
                  ? ' Consider extending the hold or improving NOI before exit to reach double-digit returns.'
                  : ' Returns are in the target range — hold strategy is on track.'}
              </p>
            </div>
          )
        })()}

        {activeTab === 'report' && (
          <div className="m16-plan-panel">
            <div className="m16-section-header">
              <h3>Professional Investor Exit Report</h3>
              <p>Finalize the documented exit strategy, timeline, return metrics, and execution checklist.</p>
            </div>

            <div className="m16-deal-summary">
              <h4>Exit Package Summary</h4>
              <div className="m16-summary-grid">
                {[
                  ['Acquisition', [['Acquired', fmtDate(closingDate)], ['Purchase Price', money(purchasePrice)], ['Loan Amount', money(loanAmount)], ['Equity Invested', money(equityInvested)]]],
                  ['Performance', [['Current NOI', money(entryNOI)], ['NOI at Exit', money(model.exitNOI)], ['Hold Period', `${model.years} years`], ['Exit Cap Rate', pct(exitCapRate, 2)]]],
                  ['Recommended Exit', [['Strategy', strategyOptions.find(s => s.id === strategy)?.label || strategy], ['Exit Value', money(model.exitValue)], ['Net Sale Proceeds', money(model.saleNet)], ['Projected IRR', pct(model.saleIRR)]]],
                  ...(ownershipCosts.totalMonthlyOwnershipCost > 0 || ownershipCosts.annualInsurance > 0 ? [['Operating Economics', [
                    ...(ownershipCosts.annualInsurance > 0 ? [[`Annual Insurance${ownershipCosts.insuranceIsEstimated ? ' (est.)' : ''}`, money(ownershipCosts.annualInsurance)]] : []),
                    ...(ownershipCosts.annualPropertyTax > 0 ? [['Annual Property Tax', money(ownershipCosts.annualPropertyTax)]] : []),
                    ...(ownershipCosts.annualPropertyOpEx > 0 ? [['Annual Op. Expenses', money(ownershipCosts.annualPropertyOpEx)]] : []),
                    ...(ownershipCosts.monthlyDebtService > 0 ? [['Monthly Debt Service', money(ownershipCosts.monthlyDebtService)]] : []),
                    ...(ownershipCosts.totalMonthlyOwnershipCost > 0 ? [['Total Monthly Cost', money(ownershipCosts.totalMonthlyOwnershipCost)]] : []),
                  ]]] : []),
                ].map(([section, rows]) => (
                  <div key={section} className="m16-summary-section">
                    <div className="m16-summary-section-title">{section}</div>
                    {rows.map(([label, val]) => (
                      <div key={label} className="m16-summary-row">
                        <span className="m16-summary-label">{label}</span>
                        <span className="m16-summary-val">{val}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div className="m16-timeline">
              <h4>Final Module Checklist</h4>
              <div className="m16-phase-steps">
                {completionSteps.map(step => (
                  <label key={step.id} className={`m16-milestone ${checkedSteps.includes(step.id) ? 'done' : ''}`}>
                    <input type="checkbox" checked={checkedSteps.includes(step.id)} onChange={() => toggleStep(step.id)} />
                    <span className="m16-milestone-dot" />
                    <span className="m16-milestone-label">{step.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="m16-form-group full">
              <label>Investor Report Notes</label>
              <textarea className="m16-textarea" rows={4} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Summarize timing rationale, market conditions, investor messaging, and next acquisition criteria." />
            </div>

          </div>
        )}
      </div>
      <div className="module-bottom-advance">
        <button className={`m16-complete-btn ${strategy && !saving ? 'ready' : 'disabled'}`} onClick={handleComplete} disabled={!strategy || saving}>
          {saving ? 'Completing Platform...' : 'Complete All Module'}
        </button>
      </div>
    </div>
  )
}
